import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useGeolocation } from '@/hooks/useGeolocation';
import {
  UseDealersInfiniteScrollOptions,
  UseDealersInfiniteScrollResult,
  useInfiniteScrollState,
  useFetchDealers,
  useInfiniteScrollActions
} from './infiniteScroll';
import { DealerFilterState } from './types';

export const useDealersInfiniteScroll = (
  options: UseDealersInfiniteScrollOptions = {}
): UseDealersInfiniteScrollResult => {
  const {
    pageSize = 20,
    enableInfiniteScroll = true,
    initialFilters = {},
    city
  } = options;

  // Disabilita il watch mode per evitare re-rendering continui
  const { coordinates } = useGeolocation({ 
    showSuccessToast: false,
    watch: false,
    maximumAge: 600000 // 10 minuti di cache
  });
  const initRef = useRef(false);
  
  // Initialize state and actions
  const { state, actions } = useInfiniteScrollState(initialFilters);
  
  // Create completely stable default filters
  const defaultFilters: DealerFilterState = useMemo(() => ({
    search: "",
    range: [0, 5] as [number, number],
    filterVerified: false,
    filterActivated: false,
    sortOrder: "distance",
    distanceRange: 0,
  }), []);
  
  // Ensure filters is always valid and stable
  const validFilters = useMemo(() => {
    const filters = actions.filters || defaultFilters;
    return filters;
  }, [actions.filters, defaultFilters]);
  
  // Create completely stable fetch options - remove state and actions dependencies
  const fetchOptions = useMemo(() => ({
    pageSize,
    filters: validFilters,
    coordinates: coordinates || null,
    city: city || undefined
  }), [pageSize, validFilters, coordinates, city]);
  
  // Initialize fetch functionality with stable options
  const { fetchDealers, abortControllerRef } = useFetchDealers({
    ...fetchOptions,
    state,
    actions
  });

  // Create completely stable loadMore function
  const stableLoadMore = useCallback(async () => {
    if (!state.hasMore || state.isLoadingMore || !enableInfiniteScroll) {
      return;
    }

    actions.setIsLoadingMore(true);
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const nextPage = state.currentPage + 1;

    try {
      await fetchDealers(nextPage, true, abortControllerRef.current.signal);
      actions.setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading page", nextPage, error);
    } finally {
      actions.setIsLoadingMore(false);
    }
  }, [state.hasMore, state.isLoadingMore, state.currentPage, enableInfiniteScroll, fetchDealers, actions.setIsLoadingMore, actions.setCurrentPage, abortControllerRef]);

  // Create stable refresh function
  const stableRefresh = useCallback(async () => {
    actions.setIsLoading(true);
    actions.resetPagination();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      await fetchDealers(0, false, abortControllerRef.current.signal);
      actions.setCurrentPage(0);
    } catch (error) {
      console.error("Error refreshing data", error);
    } finally {
      actions.setIsLoading(false);
    }
  }, [fetchDealers, actions.setIsLoading, actions.resetPagination, actions.setCurrentPage, abortControllerRef]);

  // Initial load effect - runs only once
  useEffect(() => {
    if (!enableInfiniteScroll || initRef.current) {
      return;
    }

    initRef.current = true;

    const loadInitialData = async () => {
      actions.setIsLoading(true);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        await fetchDealers(0, false, abortControllerRef.current.signal);
        actions.setCurrentPage(0);
      } catch (error) {
        console.error("Initial load error:", error);
      } finally {
        actions.setIsLoading(false);
      }
    };

    loadInitialData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enableInfiniteScroll]); // Only depend on enableInfiniteScroll

  // Filter changes effect - separate from initial load with completely stable dependencies
  useEffect(() => {
    if (!enableInfiniteScroll || !initRef.current) return;
    
    const reloadData = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        await fetchDealers(0, false, abortControllerRef.current.signal);
        actions.setCurrentPage(0);
      } catch (error) {
        console.error("Reload error:", error);
      }
    };

    reloadData();
  }, [
    enableInfiniteScroll,
    validFilters.search,
    validFilters.range?.[0],
    validFilters.range?.[1],
    validFilters.filterVerified,
    validFilters.filterActivated,
    validFilters.sortOrder,
    validFilters.distanceRange
  ]); // Only depend on filter values, not functions

  // Return completely stable object
  return useMemo(() => ({
    dealers: state.dealers || [],
    isLoading: state.isLoading || false,
    isLoadingMore: state.isLoadingMore || false,
    hasMore: state.hasMore || false,
    error: state.error || null,
    filters: validFilters,
    totalCount: state.totalCount || 0,
    updateFilters: actions.updateFilters,
    loadMore: stableLoadMore,
    refresh: stableRefresh,
    resetPagination: actions.resetPagination
  }), [
    state.dealers,
    state.isLoading,
    state.isLoadingMore,
    state.hasMore,
    state.error,
    validFilters,
    state.totalCount,
    actions.updateFilters,
    stableLoadMore,
    stableRefresh,
    actions.resetPagination
  ]);
};
